import React, { useState } from "react";
import { Routes, Route } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Divider,
  Grid,
  useMediaQuery,
  useTheme,
  Fade,
  Slide
} from "@mui/material";
import { Shield, ChartBar as BarChart3, Sparkles } from "lucide-react";
import ScoreCircularProgress from './ScoreCircularProgress';
import LifeForm from './LifeForm';
import HealthForm from './HealthForm';
import MotorForm from "./MotorForm";
import BankCASAForm from './BankCASAForm';
import BankLoanForm from './BankLoanForm';
import BankBancassuranceForm from './BankBancassuranceForm';
import BankCreditCardForm from './BankCreditCardForm';
import NBFCPersonalForm from './NBFCPersonalForm';
import NBFCHomeForm from './NBFCHomeForm';
import AgentsPage from './AgentsPage';
import LandingSection from './components/LandingSection';
import LoadingState from './components/LoadingState';
import ProfessionalHeader from './components/ProfessionalHeader';
import ProfessionalFooter from './components/ProfessionalFooter';

const personaOptions = ["Insurer", "Bank"];
const productOptionsByPersona = {
  Insurer: ["Life Insurance", "Health Insurance", "Motor Insurance"],
  NBFC: ["Personal Loan", "Home Loan"],
  Bank: ["CASA", "Loan", "Bancassurance", "Credit Cards"]
};

const ScoringPage = () => {
  const [score, setScore] = useState(null);
  const [persona, setPersona] = useState('Insurer');
  const [productType, setProductType] = useState('Life Insurance');
  const [errorMessage, setErrorMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [externalWeights, setExternalWeights] = useState(null);
  const [overrideFinalScore, setOverrideFinalScore] = useState(null);
  const [overrideFinalPay, setOverrideFinalPay] = useState(null);
  const [formulaNote, setFormulaNote] = useState(null);
  const [fixedPay, setFixedPay] = useState(null);
  const [variablePay, setVariablePay] = useState(null);

  const handlePersonaChange = (event) => {
    const nextPersona = event.target.value;
    setPersona(nextPersona);
    const firstProduct = productOptionsByPersona[nextPersona][0];
    setProductType(firstProduct);
    setScore(null);
    setErrorMessage("");
    setExternalWeights(null);
    setOverrideFinalScore(null);
    setOverrideFinalPay(null);
    setFormulaNote(null);
  };

  const handleProductTypeChange = (event) => {
    setProductType(event.target.value);
    setScore(null);
    setErrorMessage("");
    setExternalWeights(null);
    setOverrideFinalScore(null);
    setOverrideFinalPay(null);
    setFormulaNote(null);
  };

  return (
    <>
      <Grid container spacing={3} sx={{ mt: 0.5 }}>
        {/* Left Side - Form */}
        <Grid item xs={12} lg={5}>
          <Slide direction="right" in={true} timeout={800}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                overflow: 'hidden',
                boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
                height: 'fit-content',
                position: 'sticky',
                top: 20,
                border: '1px solid',
                borderColor: 'divider',
                background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 25px 70px rgba(0,0,0,0.15)'
                }
              }}
            >
              <Box
                sx={{
                  p: 2,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'relative', zIndex: 2 }}>
                  <Sparkles size={20} />
                  <Typography variant="h6" fontWeight="700">
                    AI Scoring Engine
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5, position: 'relative', zIndex: 2 }}>
                  Intelligent commission analysis powered by machine learning
                </Typography>
                
                {/* Background decoration */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: -10,
                    right: -10,
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.1)',
                    zIndex: 1
                  }}
                />
              </Box>
              
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h5" fontWeight="700" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Shield size={24} />
                    Customer Scoring Form
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Enter customer details to generate commission scores
                  </Typography>
                </Box>
              
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Shield size={20} />
                      <Typography variant="subtitle1" fontWeight={500} sx={{ ml: 1 }}>
                        Persona
                      </Typography>
                    </Box>
                    <FormControl fullWidth>
                      <Select
                        name="persona"
                        value={persona}
                        onChange={handlePersonaChange}
                        variant="outlined"
                        sx={{ 
                          borderRadius: 3,
                          '& .MuiOutlinedInput-root': {
                            background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }
                          }
                        }}
                        MenuProps={{ 
                          PaperProps: { 
                            sx: { 
                              borderRadius: 3, 
                              boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                              border: '1px solid',
                              borderColor: 'divider'
                            } 
                          } 
                        }}
                      >
                        {personaOptions.map((type) => (
                          <MenuItem 
                            key={type} 
                            value={type}
                            sx={{
                              borderRadius: 1,
                              mx: 1,
                              my: 0.5,
                              '&:hover': {
                                backgroundColor: 'primary.light',
                                color: 'primary.contrastText'
                              }
                            }}
                          >
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Shield size={20} />
                      <Typography variant="subtitle1" fontWeight={500} sx={{ ml: 1 }}>
                        Product Type
                      </Typography>
                    </Box>
                    <FormControl fullWidth>
                      <Select
                        name="product_type"
                        value={productType}
                        onChange={handleProductTypeChange}
                        variant="outlined"
                        sx={{ borderRadius: 3 }}
                        MenuProps={{ PaperProps: { sx: { borderRadius: 3, boxShadow: '0 20px 60px rgba(0,0,0,0.15)', border: '1px solid', borderColor: 'divider' } } }}
                      >
                        {productOptionsByPersona[persona].map((p) => (
                          <MenuItem key={p} value={p} sx={{ mx: 1, my: 0.5 }}>{p}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ 
                      my: 2,
                      background: 'linear-gradient(90deg, transparent, rgba(0,0,0,0.1), transparent)'
                    }} />
                  </Grid>
                </Grid>

                {persona === 'Insurer' && productType === 'Life Insurance' && (
                  <LifeForm
                    setScore={setScore}
                    setErrorMessage={setErrorMessage}
                    setOpenSnackbar={setOpenSnackbar}
                    setIsLoading={setIsLoading}
                  />
                )}
                {persona === 'Insurer' && productType === 'Health Insurance' && (
                  <HealthForm
                    setScore={setScore}
                    setErrorMessage={setErrorMessage}
                    setOpenSnackbar={setOpenSnackbar}
                    setIsLoading={setIsLoading}
                  />
                )}
                {persona === 'Insurer' && productType === 'Motor Insurance' && (
                  <MotorForm
                    setScore={setScore}
                    setErrorMessage={setErrorMessage}
                    setOpenSnackbar={setOpenSnackbar}
                    setIsLoading={setIsLoading}
                  />
                )}
                {persona === 'Bank' && productType === 'CASA' && (
                  <BankCASAForm
                    setScore={setScore}
                    onComputed={({ weights, finalScore, finalPay, formula }) => {
                      setExternalWeights(weights);
                      setOverrideFinalScore(finalScore);
                      setOverrideFinalPay(finalPay);
                      setFormulaNote(formula);
                    }}
                    setErrorMessage={setErrorMessage}
                    setOpenSnackbar={setOpenSnackbar}
                    setIsLoading={setIsLoading}
                    setFixedPay={setFixedPay}
                    setVariablePay={setVariablePay}
                  />
                )}
                {persona === 'Bank' && productType === 'Loan' && (
                  <BankLoanForm
                    setScore={setScore}
                    onComputed={({ weights, finalScore, finalPay, formula }) => {
                      setExternalWeights(weights);
                      setOverrideFinalScore(finalScore);
                      setOverrideFinalPay(finalPay);
                      setFormulaNote(formula);
                    }}
                    setErrorMessage={setErrorMessage}
                    setOpenSnackbar={setOpenSnackbar}
                    setIsLoading={setIsLoading}
                    setFixedPay={setFixedPay}
                    setVariablePay={setVariablePay}
                  />
                )}
                {persona === 'Bank' && productType === 'Bancassurance' && (
                  <BankBancassuranceForm
                    setScore={setScore}
                    onComputed={({ weights, finalScore, finalPay, formula }) => {
                      setExternalWeights(weights);
                      setOverrideFinalScore(finalScore);
                      setOverrideFinalPay(finalPay);
                      setFormulaNote(formula);
                    }}
                    setErrorMessage={setErrorMessage}
                    setOpenSnackbar={setOpenSnackbar}
                    setIsLoading={setIsLoading}
                    setFixedPay={setFixedPay}
                    setVariablePay={setVariablePay}
                  />
                )}
                {persona === 'Bank' && productType === 'Credit Cards' && (
                  <BankCreditCardForm
                    setScore={setScore}
                    onComputed={({ weights, finalScore, finalPay, formula }) => {
                      setExternalWeights(weights);
                      setOverrideFinalScore(finalScore);
                      setOverrideFinalPay(finalPay);
                      setFormulaNote(formula);
                    }}
                    setErrorMessage={setErrorMessage}
                    setOpenSnackbar={setOpenSnackbar}
                    setIsLoading={setIsLoading}
                    setFixedPay={setFixedPay}
                    setVariablePay={setVariablePay}
                  />
                )}
                {persona === 'NBFC' && productType === 'Personal Loan' && (
                  <NBFCPersonalForm
                    setScore={setScore}
                    onComputed={({ weights, finalScore, finalPay, formula }) => {
                      setExternalWeights(weights);
                      setOverrideFinalScore(finalScore);
                      setOverrideFinalPay(finalPay);
                      setFormulaNote(formula);
                    }}
                    setErrorMessage={setErrorMessage}
                    setOpenSnackbar={setOpenSnackbar}
                    setIsLoading={setIsLoading}
                    setFixedPay={setFixedPay}
                    setVariablePay={setVariablePay}
                  />
                )}
                {persona === 'NBFC' && productType === 'Home Loan' && (
                  <NBFCHomeForm
                    setScore={setScore}
                    onComputed={({ weights, finalScore, finalPay, formula }) => {
                      setExternalWeights(weights);
                      setOverrideFinalScore(finalScore);
                      setOverrideFinalPay(finalPay);
                      setFormulaNote(formula);
                    }}
                    setErrorMessage={setErrorMessage}
                    setOpenSnackbar={setOpenSnackbar}
                    setIsLoading={setIsLoading}
                    setFixedPay={setFixedPay}
                    setVariablePay={setVariablePay}
                  />
                )}
              </CardContent>
            </Card>
          </Slide>
        </Grid>

        {/* Right Side - Scores */}
        <Grid item xs={12} lg={7}>
          <Fade in={true} timeout={1000}>
            <Box>
              {isLoading ? (
                <LoadingState insuranceType={productType} />
              ) : score !== null ? (
                <Slide direction="left" in={score !== null} timeout={600}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 4,
                      overflow: 'hidden',
                      boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
                      border: '1px solid',
                      borderColor: 'divider',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 25px 70px rgba(0,0,0,0.15)'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        p: 3,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      <Typography variant="h5" fontWeight="700" sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'relative', zIndex: 2 }}>
                        <BarChart3 size={24} />
                        {productType} Analysis
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9, mt: 1, position: 'relative', zIndex: 2 }}>
                        AI-powered commission scoring and performance insights
                      </Typography>
                      
                      {/* Background decoration */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -20,
                          right: -20,
                          width: 100,
                          height: 100,
                          borderRadius: '50%',
                          background: 'rgba(255,255,255,0.1)',
                          zIndex: 1
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: -15,
                          left: -15,
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          background: 'rgba(255,255,255,0.1)',
                          zIndex: 1
                        }}
                      />
                    </Box>
                    <CardContent sx={{ p: 4 }}>
                      <ScoreCircularProgress 
                        scores={score} 
                        insuranceType={productType} 
                        externalWeights={externalWeights || undefined}
                        overrideFinalScore={overrideFinalScore ?? undefined}
                        overrideFinalPay={overrideFinalPay ?? undefined}
                        formulaNote={formulaNote ?? undefined}
                        overrideFixedPay={fixedPay ?? undefined}
                        overrideVariablePay={variablePay ?? undefined}
                      />
                    </CardContent>
                  </Card>
                </Slide>
              ) : (
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
                    height: '400px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid',
                    borderColor: 'divider',
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)'
                  }}
                >
                  <Box sx={{ textAlign: 'center', color: 'text.secondary', p: 4 }}>
                    <Box
                      sx={{
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 3,
                        border: '2px solid',
                        borderColor: 'divider',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'scale(1.05)',
                          borderColor: 'primary.main'
                        }
                      }}
                    >
                      <BarChart3 size={50} color="#64748b" />
                    </Box>
                    <Typography variant="h5" gutterBottom fontWeight="700" color="text.primary">
                      Ready to Generate Scores
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 300, mx: 'auto', lineHeight: 1.6 }}>
                      Fill out the customer form to see AI-powered commission scoring and insights
                    </Typography>
                  </Box>
                </Card>
              )}
            </Box>
          </Fade>
        </Grid>
      </Grid>
      
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity="error"
          sx={{
            borderRadius: 2,
            boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
            width: '100%'
          }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  )
}

function App() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Professional Header */}
      <ProfessionalHeader mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4, flex: 1 }}>
        <Box sx={{ maxWidth: '100%', mx: "auto" }}>
          <Routes>
            <Route 
              path="/" 
              element={<LandingSection />} 
            />
            <Route path="/score" element={<ScoringPage />} />
            <Route path="/agents" element={<AgentsPage />} />
          </Routes>
        </Box>
      </Container>

      {/* Professional Footer */}
      <ProfessionalFooter />
    </Box>
  );
}

export default App;
